import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Sigma, Loader2, BadgeCheck, GitCompareArrows, Bookmark, ArrowRight, CheckCircle2, ShieldCheck, Undo2 } from 'lucide-react';
import { toast } from 'sonner';

export default function AdHocProposalCard({ proposal, modelId, onAsk, canCertify = false }) {
  const navigate = useNavigate();
  const [certDone, setCertDone] = useState(false);
  const [certLoading, setCertLoading] = useState(false);
  const [certifiedNow, setCertifiedNow] = useState(false);
  const [certifiedMeasureId, setCertifiedMeasureId] = useState(null);
  const [certifyLoading, setCertifyLoading] = useState(false);
  const [certReason, setCertReason] = useState('');
  const [certPopoverOpen, setCertPopoverOpen] = useState(false);
  const [revertLoading, setRevertLoading] = useState(false);
  const [savedDone, setSavedDone] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [similar, setSimilar] = useState(null); // null=not fetched, []=fetched none
  const [similarLoading, setSimilarLoading] = useState(false);

  if (!proposal) return null;
  const { suggested_name, formula, definition, dimensions } = proposal;

  const dimSuffix = (dimensions || []).length
    ? ` by ${dimensions.map((d) => String(d).replace(/_/g, ' ').toLowerCase()).join(' and ')}`
    : '';

  const requestCert = async () => {
    setCertLoading(true);
    try {
      const res = await api.requestExploreCertification({
        model_id: modelId, measure_name: suggested_name, formula, business_definition: definition,
      });
      setCertDone(true);
      toast.success(res?.message || 'Certification requested');
    } catch (e) { toast.error(e.message || 'Could not request certification'); }
    finally { setCertLoading(false); }
  };

  // Steward one-click: certify this ad-hoc metric into a governed measure now,
  // with an optional certification note saved to the measure's history.
  const certifyNow = async () => {
    if (!formula) { toast.error('No formula available to certify'); return; }
    setCertifyLoading(true);
    try {
      const res = await api.adhocCertifyNow({
        model_id: modelId, measure_name: suggested_name, formula,
        business_definition: definition, note: certReason.trim() || undefined,
      });
      setCertifiedNow(true);
      setCertifiedMeasureId(res?.measure_id || null);
      setCertPopoverOpen(false);
      toast.success(res?.message || `'${suggested_name}' is now certified`, {
        action: { label: 'View in Metric Registry', onClick: () => navigate('/admin/metric-registry') },
      });
    } catch (e) { toast.error(e.message || 'Could not certify this metric'); }
    finally { setCertifyLoading(false); }
  };

  // Undo an accidental certification — revert the measure back to draft.
  const revertDraft = async () => {
    setRevertLoading(true);
    try {
      const res = await api.adhocRevertDraft({
        model_id: modelId, measure_id: certifiedMeasureId || undefined, measure_name: suggested_name,
      });
      setCertifiedNow(false);
      setCertifiedMeasureId(null);
      setCertReason('');
      toast.success(res?.message || `'${suggested_name}' reverted to draft`);
    } catch (e) { toast.error(e.message || 'Could not revert certification'); }
    finally { setRevertLoading(false); }
  };

  const savePersonal = async () => {
    setSaveLoading(true);
    try {
      const res = await api.adhocSavePersonal({
        model_id: modelId, name: suggested_name, formula, definition, dimensions: dimensions || [],
      });
      setSavedDone(true);
      toast.success(res?.message || 'Saved for your personal use');
    } catch (e) { toast.error(e.message || 'Could not save metric'); }
    finally { setSaveLoading(false); }
  };

  const compareCertified = async () => {
    setSimilarLoading(true);
    try {
      const res = await api.adhocSimilarCertified({ model_id: modelId, name: suggested_name, formula });
      setSimilar(res?.similar || []);
      if (!(res?.similar || []).length) toast.info('No similar certified measure found in this model');
    } catch (e) { toast.error(e.message || 'Could not look up certified measures'); }
    finally { setSimilarLoading(false); }
  };

  return (
    <div className="mb-2 rounded-xl border border-amber-500/30 bg-amber-500/5 p-3" data-testid="adhoc-proposal-card">
      <div className="flex items-start gap-2">
        <Sigma className="w-4 h-4 mt-0.5 flex-shrink-0 text-amber-500" />
        <div className="flex-1 min-w-0">
          <p className="text-xs font-medium text-amber-800 dark:text-amber-200" data-testid="proposal-name">
            Ad-hoc metric: {suggested_name}
          </p>
          {formula && (
            <p className="text-[11px] font-mono text-muted-foreground mt-0.5 break-words" data-testid="proposal-formula">{formula}</p>
          )}
          <p className="text-[11px] text-muted-foreground mt-0.5 leading-relaxed">{definition}</p>

          <div className="flex items-center gap-2 mt-2 flex-wrap">
            <Button size="sm" variant="outline"
              className="h-6 px-2.5 text-[10px] rounded-full border-blue-500/40 text-blue-700 dark:text-blue-300 hover:bg-blue-500/10"
              onClick={compareCertified} disabled={similarLoading} data-testid="proposal-compare-certified">
              {similarLoading ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <GitCompareArrows className="w-3 h-3 mr-1" />}
              Compare with certified
            </Button>
            {canCertify ? (
              certifiedNow ? (
                <span className="inline-flex items-center gap-1.5">
                  <span className="text-[10px] font-medium text-emerald-700 dark:text-emerald-300 inline-flex items-center gap-1" data-testid="proposal-certified-now">
                    <ShieldCheck className="w-3 h-3" /> Certified
                  </span>
                  <Button size="sm" variant="ghost"
                    className="h-6 px-2 text-[10px] rounded-full text-muted-foreground hover:text-foreground hover:bg-muted/40"
                    onClick={revertDraft} disabled={revertLoading} data-testid="proposal-undo-certify"
                    title="Revert this measure back to draft">
                    {revertLoading ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Undo2 className="w-3 h-3 mr-1" />}
                    Undo
                  </Button>
                </span>
              ) : (
                <Popover open={certPopoverOpen} onOpenChange={setCertPopoverOpen}>
                  <PopoverTrigger asChild>
                    <Button size="sm" variant="outline"
                      className="h-6 px-2.5 text-[10px] rounded-full border-emerald-500/60 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-500/20 font-semibold"
                      disabled={certifyLoading || !formula} data-testid="proposal-certify-now"
                      title="Certify this metric into a governed measure now">
                      {certifyLoading ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <ShieldCheck className="w-3 h-3 mr-1" />}
                      Certify
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent align="start" className="w-72 p-3" data-testid="proposal-certify-popover">
                    <p className="text-xs font-medium mb-1">Certify “{suggested_name}”</p>
                    <p className="text-[11px] text-muted-foreground mb-2">Add a short note for the certification history (optional).</p>
                    <Textarea
                      value={certReason} onChange={(e) => setCertReason(e.target.value)}
                      placeholder="e.g. Validated against Q1 board pack" rows={2}
                      className="text-[11px] mb-2" data-testid="proposal-certify-note" />
                    <div className="flex justify-end gap-2">
                      <Button size="sm" variant="ghost" className="h-6 px-2 text-[10px]"
                        onClick={() => setCertPopoverOpen(false)} data-testid="proposal-certify-cancel">Cancel</Button>
                      <Button size="sm"
                        className="h-6 px-2.5 text-[10px] bg-emerald-600 hover:bg-emerald-700 text-white"
                        onClick={certifyNow} disabled={certifyLoading} data-testid="proposal-certify-confirm">
                        {certifyLoading ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <ShieldCheck className="w-3 h-3 mr-1" />}
                        Certify measure
                      </Button>
                    </div>
                  </PopoverContent>
                </Popover>
              )
            ) : (certDone ? (
              <span className="text-[10px] font-medium text-emerald-700 dark:text-emerald-300 inline-flex items-center gap-1" data-testid="proposal-cert-requested">
                <CheckCircle2 className="w-3 h-3" /> Certification requested
              </span>
            ) : (
              <Button size="sm" variant="outline"
                className="h-6 px-2.5 text-[10px] rounded-full border-emerald-500/40 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-500/10"
                onClick={requestCert} disabled={certLoading} data-testid="proposal-request-cert">
                {certLoading ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <BadgeCheck className="w-3 h-3 mr-1" />}
                Request certification
              </Button>
            ))}
            {savedDone ? (
              <span className="text-[10px] font-medium text-emerald-700 dark:text-emerald-300 inline-flex items-center gap-1" data-testid="proposal-saved">
                <CheckCircle2 className="w-3 h-3" /> Saved
              </span>
            ) : (
              <Button size="sm" variant="ghost"
                className="h-6 px-2.5 text-[10px] rounded-full text-muted-foreground hover:text-foreground hover:bg-muted/40"
                onClick={savePersonal} disabled={saveLoading} data-testid="proposal-save-personal">
                {saveLoading ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Bookmark className="w-3 h-3 mr-1" />}
                Save for personal use
              </Button>
            )}
          </div>

          {similar !== null && similar.length > 0 && (
            <div className="mt-2 pt-2 border-t border-amber-500/20" data-testid="proposal-similar-list">
              <p className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">Similar certified measures</p>
              <div className="flex flex-col gap-1">
                {similar.map((s, i) => (
                  <button key={s.name}
                    onClick={() => onAsk && onAsk(`${s.label}${dimSuffix}`)}
                    className="text-left text-[11px] flex items-center gap-1.5 px-2 py-1 rounded-md border border-emerald-500/30 bg-emerald-500/5 hover:bg-emerald-500/10 text-emerald-800 dark:text-emerald-200 transition-colors"
                    data-testid={`proposal-use-certified-${i}`}
                    title={s.description || s.formula || ''}>
                    <ArrowRight className="w-3 h-3 flex-shrink-0" />
                    <span className="font-medium">{s.label}</span>
                    {s.formula ? <span className="font-mono text-muted-foreground truncate">— {s.formula}</span> : null}
                  </button>
                ))}
              </div>
            </div>
          )}
          {similar !== null && similar.length === 0 && (
            <p className="mt-2 text-[10px] text-muted-foreground" data-testid="proposal-similar-empty">
              No similar certified measure in this model — request certification to govern it.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

export { AdHocProposalCard };
